// src/pages/FeaturesPage.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";
import PagesTopNav from "../components/TopNav/PagesTopNav";
import { useAuth } from "../context/AuthContext";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import PageLayout from "../components/PageLayout";
import styled from "styled-components";
import notify from "../utils/notify";

// Styled Components
const FeaturesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  padding: 2rem 0;
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
  }
`;

const IconBox = styled.div`
  font-size: 2.2rem;
  color: #4a6cf7;
  margin-bottom: 1.25rem;
`;

const FeatureTitle = styled.h4`
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  color: #222;
  position: relative;
`;

const FeatureText = styled.div`
  color: #555;
  line-height: 1.7;
`;

const EditInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 0.5rem;
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  resize: vertical;
`;

const EditControls = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #4a6cf7;
  font-size: 1rem;
  transition: transform 0.2s ease;

  &:hover { transform: scale(1.1); }
  &.cancel { color: #ff4757; }
`;

// Inline alert (no toast for visitors)
const InlineAlert = styled.div`
  margin: 0 0 1rem;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: .95rem;
  border: 1px solid ${(p) => (p.$variant === "error" ? "#fbd5d5" : "#c7e2ff")};
  background: ${(p) => (p.$variant === "error" ? "#fde8e8" : "#eef6ff")};
  color: ${(p) => (p.$variant === "error" ? "#9b1c1c" : "#1e3a8a")};
`;

const FeaturesPage = () => {
  const { authData } = useAuth();
  const roles = authData?.role;
  const isAdmin = Array.isArray(roles)
    ? roles.includes("SystemAdmin")
    : roles === "SystemAdmin";

  const [content, setContent] = useState({});
  const [editingLabel, setEditingLabel] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [loadError, setLoadError] = useState("");

  // Fetch all page content once; map to { label: content }
  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get(`/PageContent`)
      .then((res) => {
        if (!mounted) return;
        const map = Array.isArray(res.data)
          ? res.data.reduce((acc, x) => {
              acc[x.label] = x.content;
              return acc;
            }, {})
          : {};
        setContent(map);
        setLoadError("");
      })
      .catch((err) => {
        console.error("Failed to load features content:", err);
        if (!mounted) return;
        // No toast for visitors — inline alert
        setLoadError("We’re having trouble loading features right now.");
      });
    return () => { mounted = false; };
  }, []);

  const handleEdit = (label) => {
    setEditedContent(content[label] || "");
    setEditingLabel(label);
  };

  const handleCancel = () => {
    setEditingLabel(null);
    setEditedContent("");
  };

  const handleSave = async (label) => {
    try {
      await axiosInstance.put(
        `/PageContent/${label}`,
        { content: editedContent },
        {
          headers: {
            Authorization: `Bearer ${authData?.token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setContent((prev) => ({ ...prev, [label]: editedContent }));
      setEditingLabel(null);
      // Admin-only feedback via toast
      if (isAdmin) notify.success("Content updated successfully!");
    } catch (error) {
      console.error("Failed to update content:", error);
      if (isAdmin) notify.error("Failed to update content.");
    }
  };

  const renderEditControls = (label) =>
    isAdmin && (
      <EditControls>
        {editingLabel === label ? (
          <>
            <IconButton onClick={() => handleSave(label)} aria-label="Save">
              <FaSave />
            </IconButton>
            <IconButton onClick={handleCancel} className="cancel" aria-label="Cancel">
              <FaTimes />
            </IconButton>
          </>
        ) : (
          <IconButton onClick={() => handleEdit(label)} aria-label="Edit">
            <FaEdit />
          </IconButton>
        )}
      </EditControls>
    );

  const featureItems = [
    {
      icon: "fas fa-calendar-check",
      titleLabel: "feature1-title",
      textLabel: "feature1-text",
      defaultTitle: "Booking Management",
      defaultText:
        "Comprehensive tools to manage all reservations, availability, and bookings with real-time updates.",
    },
    {
      icon: "fas fa-broom",
      titleLabel: "feature2-title",
      textLabel: "feature2-text",
      defaultTitle: "Housekeeping",
      defaultText:
        "Automated cleaning schedules and staff assignments for efficient room turnover.",
    },
    {
      icon: "fas fa-concierge-bell",
      titleLabel: "feature3-title",
      textLabel: "feature3-text",
      defaultTitle: "Reservation System",
      defaultText:
        "Seamless booking experience with online and offline reservation capabilities.",
    },
    {
      icon: "fas fa-door-open",
      titleLabel: "feature4-title",
      textLabel: "feature4-text",
      defaultTitle: "Digital Check-In",
      defaultText:
        "Streamlined check-in process with digital forms and mobile compatibility.",
    },
    {
      icon: "fas fa-door-closed",
      titleLabel: "feature5-title",
      textLabel: "feature5-text",
      defaultTitle: "Express Check-Out",
      defaultText:
        "Automated check-out with digital receipts and feedback collection.",
    },
    {
      icon: "fas fa-utensils",
      titleLabel: "feature6-title",
      textLabel: "feature6-text",
      defaultTitle: "Room Service",
      defaultText: "Integrated room service ordering and tracking system.",
    },
  ];

  return (
    <div>
      <PagesTopNav />

      <PageLayout
        title="Our Features"
        description="Discover the powerful tools that make our hotel management system stand out."
      >
        {loadError && (
          <InlineAlert role="status" aria-live="polite" $variant="error">
            {loadError}
          </InlineAlert>
        )}

        <FeaturesContainer>
          {featureItems.map((feature, idx) => (
            <FeatureCard key={idx}>
              <IconBox>
                <i className={feature.icon} aria-hidden="true"></i>
              </IconBox>

              <FeatureTitle>
                {editingLabel === feature.titleLabel && isAdmin ? (
                  <EditInput
                    type="text"
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    aria-label={`Edit ${feature.defaultTitle} Title`}
                  />
                ) : (
                  content[feature.titleLabel] || feature.defaultTitle
                )}
                {renderEditControls(feature.titleLabel)}
              </FeatureTitle>

              <FeatureText>
                {editingLabel === feature.textLabel && isAdmin ? (
                  <EditTextarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    aria-label={`Edit ${feature.defaultTitle} Text`}
                  />
                ) : (
                  content[feature.textLabel] || feature.defaultText
                )}
                {renderEditControls(feature.textLabel)}
              </FeatureText>
            </FeatureCard>
          ))}
        </FeaturesContainer>
      </PageLayout>
    </div>
  );
};

export default FeaturesPage;
